/**
 * Shared QueryClient config.
 *
 * Exports a factory so the app wires one per render tree (T7). We do NOT
 * instantiate a singleton at module scope — module scope is not survivable
 * across SSR / testing / multi-tree setups.
 *
 * Defaults picked for an offline-first shopping app:
 *  - `staleTime: 30_000` — treat cached data as fresh for 30s so immediate
 *    refetch storms on re-mount don't hit the backend. Tune upward once the
 *    sync layer (T5) lands and starts invalidating proactively.
 *  - `gcTime: 5 * 60_000` — keep inactive cache for 5 minutes (TanStack Query
 *    v5 calls this `gcTime`, formerly `cacheTime`).
 *  - `retry: (failureCount, err) => …` — never retry auth errors; retry other
 *    failures once. The HttpClient already handles 401-refresh-retry at a
 *    lower level, so TanStack retry would double up.
 *  - `refetchOnWindowFocus: true` — SSE/invalidation stays the instant path;
 *    focus-refetch is the safety net for a window that was backgrounded or
 *    briefly disconnected and missed an event. `staleTime: 30_000` throttles
 *    it, so at most one refetch per query per focus per 30s — no refetch storm.
 */

import { QueryClient, type QueryClientConfig } from '@tanstack/react-query';
import { ApiError } from './errors';

export function createQueryClient(overrides?: QueryClientConfig): QueryClient {
  const config: QueryClientConfig = {
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          // Auth failures — let the HTTP client's refresh flow resolve them,
          // and let our caller (UI) decide whether to route to login.
          if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
            return false;
          }
          // 4xx (non-auth) is almost never retry-friendly.
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
            return false;
          }
          return failureCount < 1;
        },
      },
      mutations: {
        retry: false,
      },
      ...overrides?.defaultOptions,
    },
  };
  return new QueryClient(config);
}
